import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import "./CuteStar.css";
import { useSelector, useDispatch } from 'react-redux';
import { cuteStarActions } from 'src/store/slices/cuteStarSlice';
import {
  selectStarExpression,
  selectStarMessage,
  selectStarShowBubble,
  selectStarPosition,
  selectStarPushCount
} from 'src/store/slices/cuteStarSlice';

/*
  Props:
    size: number
    expression: 'idle' | 'happy' | 'wink' | 'talk' | 'excited'
    message: string
    floating: bool
    pulse: bool
    disableEscape: bool (opsiyonel - true ise kaçma davranışı kapalı)
*/
const CuteStar = ({
  size = 140,
  expression,
  message,
  floating = true,
  pulse = false,
  disableEscape = false,
  escapeMinDistance,
  className = "",
  style,
  fixedPosition, // YENİ: {left, top} verildiğinde random & escape kapatılır
}) => {
  // Slice state
  const dispatch = useDispatch();
  const storeExpression = useSelector(selectStarExpression);
  const storeMessage = useSelector(selectStarMessage);
  const storeShowBubble = useSelector(selectStarShowBubble);
  const storedPosition = useSelector(selectStarPosition);
  const pushCount = useSelector(selectStarPushCount);

  const effectiveExpression = expression !== undefined ? expression : (storeExpression || 'happy');
  const effectiveMessage = message !== undefined ? message : storeMessage;
  // ESKİ: const showBubble = storeShowBubble && effectiveMessage;
  const showBubble = (storeShowBubble !== false) && !!effectiveMessage; // slice yoksa veya undefined ise balon göster

  const mouthPath = useMemo(() => {
    switch (effectiveExpression) {
      case "happy": return "M52 70 Q60 78 68 70";
      case "wink": return "M52 70 Q60 77 68 70";
      case "talk": return "M52 69 Q60 85 68 69";
      case "excited": return "M50 68 Q60 90 70 68";
      case "cry": return "M52 74 Q60 66 68 74";        // üzgün ters kavis
      case "surprised": return "M56 66 Q60 72 64 66";  // küçük oval ağzın üst yayı
      default: return "M52 70 Q60 75 68 70";
    }
  }, [effectiveExpression]);

  const rightEyeBlinkClass = effectiveExpression === "wink" ? "cute-star__eye--blink" : "";

  // Sağ/sol gözlerde aynı yapı; blink her iki gözde de (wink modunda sağ göz özel animasyon)
  const leftBlinkClass = effectiveExpression === "wink" ? "cute-star__eye--blink" : "cute-star__eye--blink";
  const rightBlinkClass = effectiveExpression === "wink" ? "" : "cute-star__eye--blink";

  const minDistance = escapeMinDistance !== undefined ? escapeMinDistance : size * 0.7;

  const rootRef = useRef(null);
  const [posOverride, setPosOverride] = useState(null);
  const [animPos, setAnimPos] = useState(null);
  const animRef = useRef();
  const [escaping, setEscaping] = useState(false);
  const lastEscapeRef = useRef(0);
  const reactionHandledRef = useRef(false); // YENİ: 5. itme reaksiyonu bir kez çalışsın
  const timeoutRef = useRef(null); // 10 sn sonra happy'e dönme zamanlayıcısı

  // Basit random konum (parent boyutlarına göre)
  const randomPosition = useCallback(() => {
    const el = rootRef.current;
    const parent = el?.offsetParent || el?.parentElement || document.body;
    const rect = parent.getBoundingClientRect();
    const pad = 10;
    const maxLeft = Math.max(0, rect.width - size - pad);
    const maxTop = Math.max(0, rect.height - size - pad);
    return {
      left: Math.random() * maxLeft,
      top: Math.random() * maxTop
    };
  }, [size]);

  // İlk konum: slice pozisyonu varsa onu, yoksa rastgele (görünür olmadan önce)
  useEffect(() => {
    if (fixedPosition) {
      // Sabit konum
      setPosOverride(fixedPosition);
      setAnimPos(fixedPosition);
      dispatch(cuteStarActions.setPosition(fixedPosition));
      return;
    }
    if (animPos || posOverride) return;
    const assign = () => {
      let initial = storedPosition &&
        typeof storedPosition.left === 'number' &&
        typeof storedPosition.top === 'number'
        ? storedPosition
        : randomPosition();
      setPosOverride(initial);
      setAnimPos(initial);
      dispatch(cuteStarActions.setPosition(initial));
    };
    // parent ölçüsü hazır olsun diye rAF
    requestAnimationFrame(assign);
  }, [storedPosition, animPos, posOverride, randomPosition, dispatch, fixedPosition]);

  // fixedPosition runtime’da değişirse güncelle
  useEffect(() => {
    if (fixedPosition) {
      setPosOverride(fixedPosition);
      setAnimPos(fixedPosition);
      dispatch(cuteStarActions.setPosition(fixedPosition));
    }
  }, [fixedPosition, dispatch]);

  // Lerp (yumuşak geçiş)
  useEffect(() => {
    if (fixedPosition) return; // sabit modda animasyonlu takip yok
    if (!posOverride || !animPos) return;
    cancelAnimationFrame(animRef.current);
    const step = () => {
      setAnimPos(prev => {
        if (!prev) return posOverride;
        const dx = posOverride.left - prev.left;
        const dy = posOverride.top - prev.top;
        const dist = Math.hypot(dx, dy);
        if (dist < 0.8) return posOverride;
        const s = 0.18; // sabit hız
        return { left: prev.left + dx * s, top: prev.top + dy * s };
      });
      animRef.current = requestAnimationFrame(step);
    };
    if (animPos.left !== posOverride.left || animPos.top !== posOverride.top) {
      animRef.current = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [posOverride, animPos, fixedPosition]);

  // Sade kaçış: merkez - mouse doğrultusunda sabit küçük adım
  const smartEscape = useCallback((evt) => {
    const el = rootRef.current;
    if (!el) return randomPosition();
    const parent = el.offsetParent || el.parentElement || document.body;
    const rect = parent.getBoundingClientRect();
    const prev = posOverride || { left: 0, top: 0 };

    const centerX = rect.left + prev.left + size / 2;
    const centerY = rect.top + prev.top + size / 2;

    let vx = centerX - evt.clientX;
    let vy = centerY - evt.clientY;
    let len = Math.hypot(vx, vy);
    if (len < 0.0001) { // tam üstü
      vx = (Math.random() - 0.5);
      vy = (Math.random() - 0.5);
      len = Math.hypot(vx, vy);
    }
    vx /= len; vy /= len;

    const ESCAPE_STEP = Math.max(40, size * 0.9);
    let left = prev.left + vx * ESCAPE_STEP;
    let top  = prev.top + vy * ESCAPE_STEP;

    // Sınır clamp
    const maxLeft = rect.width - size - 10;
    const maxTop  = rect.height - size - 10;
    left = Math.min(Math.max(5, left), maxLeft);
    top  = Math.min(Math.max(5, top), maxTop);

    const movedDist = Math.hypot(left - prev.left, top - prev.top);
    const nearEdge = left < 15 || top < 15 || left > maxLeft - 15 || top > maxTop - 15;

    if (movedDist < minDistance * 0.4 || nearEdge) {
      return randomPosition();
    }
    return { left, top };
  }, [posOverride, size, minDistance, randomPosition]);

  const tryEscape = useCallback((evt) => {
    if (disableEscape || fixedPosition) return;
    const now = Date.now();
    if (now - lastEscapeRef.current < 250) return;
    lastEscapeRef.current = now;
    setEscaping(true);
    setPosOverride(prev => smartEscape(evt) || prev);

    // YENİ: push sayacı
    dispatch(cuteStarActions.incrementPush());
    if (pushCount + 1 >= 5) {
      dispatch(cuteStarActions.setShowBubble(false));
    }

    setTimeout(() => setEscaping(false), 300);
  }, [disableEscape, smartEscape, dispatch, pushCount, fixedPosition]);

  const handleMouseEnter = tryEscape;
  const handleMouseMove = tryEscape;

  // Stil: pozisyon hazır değilse görünmez
  const isPositioned = !!animPos;
  const mergedStyle = {
    width: size,
    height: size,
    position: style?.position || (fixedPosition ? 'absolute' : 'absolute'),
    ...(style || {}),
    ...(isPositioned
      ? { left: animPos.left, top: animPos.top }
      : { visibility: 'hidden' } // İlk random hesaplanana kadar gösterme
    ),
    ...(escaping && isPositioned
      ? { transform: 'scale(1.08) rotate(3deg)', transition: 'transform 180ms ease' }
      : {})
  };

  // 5. push sonrası rastgele 'cry' veya 'surprised', 10 sn sonra 'happy'
  useEffect(() => {
    if (fixedPosition) return; // sabit modda push count reaksiyonu gereksiz
    // pushCount resetlenmişse (örn. slice reset) her şeyi sıfırla
    if (pushCount < 5) {
      if (reactionHandledRef.current) reactionHandledRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }
    // Sadece tam 5 olduğunda bir kere tetikle
    if (pushCount === 5 && !reactionHandledRef.current) {
      reactionHandledRef.current = true;
      const next = Math.random() < 0.5 ? 'cry' : 'surprised';
      dispatch(cuteStarActions.setExpression(next));
      timeoutRef.current = setTimeout(() => {
        dispatch(cuteStarActions.setExpression('happy'));
        timeoutRef.current = null;
      }, 10000);
    }
    // pushCount 5'ten büyük olduğunda timeout bozulmadan akar
  }, [pushCount, dispatch, fixedPosition]);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Not: Parent expression prop geçerse (ör: <CuteStar expression="wink" />),
  // slice üzerinden setExpression('happy') sonucu görünmez çünkü prop öncelikli.
  // Bu durumda prop'u kaldır ya da kontrol ekle.

  // Root class list (önceden silinmişti) – no-undef hatasını giderir
  const rootClasses = [
    'cute-star-mascot',
    floating ? 'cute-star--floating' : '',
    pulse ? 'cute-star--pulse' : '',
    effectiveExpression === 'talk' ? 'cute-star--talking' : '',
    effectiveExpression === 'excited' ? 'cute-star--excited' : '',
    effectiveExpression === 'happy' ? 'cute-star--happy' : '',
    effectiveExpression === 'wink' ? 'cute-star--wink' : '',
    escaping ? 'cute-star--escaping' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={rootRef}
      className={rootClasses}
      style={mergedStyle}
      aria-label="Friendly animated star mascot"
      role="img"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
    >
      <svg
        className="cute-star__svg"
        viewBox="0 0 120 120"
        width={size}
        height={size}
      >
        <defs>
          <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="110%">
            <stop offset="0%" stopColor="#ffe587" />
            <stop offset="45%" stopColor="#ffc933" />
            <stop offset="100%" stopColor="#ffb51f" />
          </linearGradient>
          <linearGradient id="innerGlow" x1="30%" y1="25%" x2="70%" y2="85%">
            <stop offset="0%" stopColor="#fff6d6" stopOpacity=".95" />
            <stop offset="55%" stopColor="#ffdf8a" stopOpacity=".35" />
            <stop offset="100%" stopColor="#ffc23d" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="cheekGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffad5f" />
            <stop offset="60%" stopColor="#ff8f4d" />
            <stop offset="100%" stopColor="#ff7d43" />
          </radialGradient>
          <radialGradient id="eyeIrisGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffeeb5" />
            <stop offset="55%" stopColor="#f5c13a" />
            <stop offset="100%" stopColor="#d38a14" />
          </radialGradient>
          <linearGradient id="tearGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#cfefff"/>
            <stop offset="55%" stopColor="#7dd3ff"/>
            <stop offset="100%" stopColor="#2b9be2"/>
          </linearGradient>
        </defs>
        <g className="cute-star__core">
          <path
            d="M60 6
               C63 14 68 30 70 34
               C71 36 73 38 75 39
               C86 41 102 44 111 46
               C113 46.5 114 49 112 51
               C106 56 95 64 89 68
               C86 70 85 73 86 76
               C88 85 91 98 92 105
               C92.4 107.5 89.5 110 87 108.5
               C79 103.5 66 94.5 61 91
               C59.5 90 58 90 56.5 91
               C51 95 38 104 31 109
               C28.5 110.8 25.8 108.5 26.4 105.8
               C28 98 31 84.5 33 76
               C34 72.5 33 69.5 30 67
               C23.5 62 12 54 8 50
               C6 48 6.5 45 9 44
               C16.5 42 33 39 43 37
               C45.5 36.5 47.5 34.5 48.5 32
               C51 26 56 13 58 7
               C58.7 5.2 59.3 5.2 60 6 Z"
            fill="url(#starGradient)"
            stroke="#6a4000"
            strokeWidth="3.2"
            strokeLinejoin="round"
          />
          <path
            d="M60 14
               C62 20 66 32 68 36
               C69 38 71 40 73 41
               C82 43 95 45 102 47
               C95 53 86 59 81 63
               C77 66 75 70 76 75
               C78 83 81 95 82 100
               C74 95 66 90 60 86
               C58 85 56 85 54 86
               C48 90 38 97 33 101
               C34 95 36 82 38 74
               C39 70 37 65 33 62
               C27 57 17 50 14 48
               C22 46.7 35 44.5 44 43
               C47 42.5 49 40.5 50 38
               C52 33 56 22 58 16
               C58.8 13.8 59.2 13.8 60 14 Z"
            fill="url(#innerGlow)"
            opacity=".85"
          />
          <g className="cute-star__face">
            <g transform="translate(48 58)">
              <ellipse className="cute-star__eye-core" cx="0" cy="0" rx={effectiveExpression === 'surprised' ? 6 : 5} ry={effectiveExpression === 'surprised' ? 7.5 : 6.4} />
              <circle className="cute-star__eye-highlight2" cx="-1.4" cy="-2.1" r="1.15" />
            </g>
            <g transform="translate(72 58)">
              <ellipse className="cute-star__eye-core" cx="0" cy="0" rx={effectiveExpression === 'surprised' ? 6 : 5} ry={effectiveExpression === 'surprised' ? 7.5 : 6.4} />
              <circle className="cute-star__eye-highlight2" cx="-1.4" cy="-2.1" r="1.15" />
            </g>
            {/* Cheeks adjusted inward & slightly smaller to avoid overflow */}
            <circle className="cute-star__cheek" cx="44" cy="69.5" r="5.2" />
            <circle className="cute-star__cheek" cx="76" cy="69.5" r="5.2" />
            {effectiveExpression === 'surprised' && (
              <circle className="cute-star__mouth-surprised" cx="60" cy="69" r="6.2" />
            )}
            <path
              className={`cute-star__mouth ${effectiveExpression === 'cry' ? 'cute-star__mouth--cry' : ''}`}
              d={mouthPath}
              strokeWidth={
                effectiveExpression === "excited"
                  ? 3.4
                  : effectiveExpression === 'cry'
                  ? 3
                  : 3.2
              }
            />
            {effectiveExpression === 'cry' && (
              <g className="cute-star__tears">
                {/* Sol göz */}
                <circle className="cute-star__tear t1" cx="48" cy="66" r="3" />
                <circle className="cute-star__tear t2" cx="48" cy="66" r="3" />
                <circle className="cute-star__tear t3" cx="48" cy="66" r="3" />
                {/* Sağ göz */}
                <circle className="cute-star__tear t1" cx="72" cy="66" r="3" />
                <circle className="cute-star__tear t2" cx="72" cy="66" r="3" />
                <circle className="cute-star__tear t3" cx="72" cy="66" r="3" />
              </g>
            )}
          </g>
        </g>
      </svg>

      {showBubble && (
        <div className="cute-star__bubble" role="status">
          <div className="cute-star__cloud">
            <span className="cloud c1" />
            <span className="cloud c2" />
            <span className="cloud c3" />
            <span className="cloud c4" />
            <span className="cloud c5" />
            <div className="cute-star__bubble-text">
              {effectiveMessage}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuteStar;